import { Inject, Injectable, Optional } from '@angular/core';
import { Store } from '@ngrx/store';
import { CONSTANTS, ChatNotificationLevelEnum, Constants } from 'lib-trend-core';
import { CHAT_SURFACE, ChatClientSurfaceEnum } from '../chat.tokens';
import {
  directChannelIdsSelector,
  notificationLevelsSelector,
  quietHoursSelector,
  soundEnabledSelector,
} from '../state/chat.selectors';

/** Alert asset already shipped for attendance notifications. */
// Chat-specific tone (two-note ding). The platform's attendance alert keeps
// `alert-message.mp3` — the internal chat must not sound like an attendance
// (DEV-1984); to change the chat sound, replace this asset.
const ALERT_SOUND_URL = 'assets/audio/chat-message.wav';

/** "HH:mm" 24h clock — the only accepted quiet-hours boundary format (RF-36). */
const HOUR_MINUTE_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Company entity default — mirrors the backend fallback. */
const DEFAULT_TIMEZONE = 'America/Sao_Paulo';

/** What the caller knows about the incoming message. */
export interface ChatNotifyContext {
  /** The message mentions me (`chat:unread:updated.mentioned`). */
  mentioned?: boolean;
}

/**
 * Client-side notification gate (SPEC-2026-07-internal-chat, T-21 · RF-28/RF-36
 * · AC-24/AC-32) — the single place that decides whether an incoming chat
 * message may raise an in-app alert on THIS client.
 *
 * Mirrors the backend push gating (`delivery.service` + `settings.service`) so
 * in-app toast/sound and push never disagree:
 * - level `NONE` → never alerts;
 * - level `MENTIONS` → alerts only for mentions and direct messages;
 * - quiet hours (evaluated in the COMPANY timezone, midnight-crossing windows
 *   included) → suppresses alert and sound;
 * - sound additionally requires `soundEnabled`.
 *
 * - alert priority (SPEC-2026-09-internal-chat-pwa, RF-21): while the user has a
 *   chat app session connected, the PRODUCT never raises a sound or a pop-up —
 *   the chat app is where the user reads chat, so the alert rings there once
 *   instead of twice. The chat app itself is never muted by this rule.
 *
 * Unread counters are never gated — they keep accumulating (RF-28/RF-36).
 *
 * Consumers:
 * - `ChatSocketBridgeService` plays the alert sound on the new-message fan-out;
 * - `ChatPushService.handleForegroundMessage()` gates the FCM foreground toast
 *   (wired by T-24; the mention flag comes from the push `data.type`);
 * - `ChatBadgeService.canNotify()` re-exposes the check for any other surface
 *   that raises toasts.
 */
@Injectable({ providedIn: 'root' })
export class ChatNotificationGateService {

  private levels: Record<string, ChatNotificationLevelEnum> = {};
  private directChannelIds = new Set<string>();
  private soundEnabled = true;
  private quietHours: { enabled: boolean, start: string, end: string } = { enabled: false, start: '', end: '' };

  /** Created on the first allowed alert — nothing is fetched otherwise. */
  private alertAudio: HTMLAudioElement | null = null;

  /** Last server surface signal: a chat app session of this user is connected. */
  private chatAppConnected = false;

  constructor(
    private readonly store: Store,
    @Optional() @Inject(CONSTANTS) private readonly constants: Constants | null,
    @Optional() @Inject(CHAT_SURFACE) private readonly surface: ChatClientSurfaceEnum | null,
  ) {
    // Root-scoped singleton for the app's lifetime — the store snapshots are
    // kept live so `canNotify()` stays synchronous for its callers.
    this.store.select(notificationLevelsSelector).subscribe((levels) => this.levels = levels);
    this.store.select(directChannelIdsSelector).subscribe((ids) => this.directChannelIds = new Set(ids));
    this.store.select(soundEnabledSelector).subscribe((enabled) => this.soundEnabled = enabled);
    this.store.select(quietHoursSelector).subscribe((quietHours) => this.quietHours = quietHours);
  }

  /**
   * Fed by the `chat:surface:updated` signal (snapshot on join + transitions). Only
   * the product surface acts on it; see `isMutedBySurface`.
   */
  setChatAppConnected(connected: boolean): void {
    this.chatAppConnected = !!connected;
  }

  /** True while alerts on THIS surface yield to a connected chat app session. */
  isMutedBySurface(): boolean {
    return this.chatAppConnected
      && (this.surface ?? ChatClientSurfaceEnum.WEB_APP) === ChatClientSurfaceEnum.WEB_APP;
  }

  /** May an alert (toast/sound) be raised for this conversation? Counters are never gated. */
  canNotify(channelId: string, context: ChatNotifyContext = {}): boolean {
    if (!channelId) return false;
    if (this.isMutedBySurface()) return false;

    const level = this.levels[channelId] ?? ChatNotificationLevelEnum.ALL;
    if (level === ChatNotificationLevelEnum.NONE) return false;
    if (level === ChatNotificationLevelEnum.MENTIONS
      && !context.mentioned
      && !this.directChannelIds.has(channelId)) {
      return false;
    }

    return !this.isQuietNow();
  }

  /** `canNotify` plus the sound preference (RF-36). */
  canPlaySound(channelId: string, context: ChatNotifyContext = {}): boolean {
    return this.soundEnabled && this.canNotify(channelId, context);
  }

  /** Plays the short alert when every gate allows it — silent otherwise. */
  playAlert(channelId: string, context: ChatNotifyContext = {}): void {
    if (!this.canPlaySound(channelId, context)) return;

    try {
      if (!this.alertAudio) {
        this.alertAudio = new Audio(ALERT_SOUND_URL);
      }
      this.alertAudio.currentTime = 0;
      // Autoplay may be blocked until the first user gesture — never throws out.
      this.alertAudio.play().catch(() => { /* best effort */ });
    } catch {
      /* best effort — an alert sound must never break the socket stream */
    }
  }

  /**
   * Quiet-hours window check evaluated in the company timezone (start
   * inclusive, end exclusive; a window such as 22:00–07:00 wraps midnight).
   * `now` is injectable to keep checks deterministic.
   */
  isQuietNow(now: Date = new Date()): boolean {
    if (!this.quietHours.enabled) return false;

    const { start, end } = this.quietHours;
    if (!HOUR_MINUTE_PATTERN.test(start) || !HOUR_MINUTE_PATTERN.test(end) || start === end) {
      return false;
    }

    // "HH:mm" compares chronologically as a plain string.
    const current = this.currentHourMinute(now);
    return start < end
      ? (current >= start && current < end)
      : (current >= start || current < end);
  }

  /** Company timezone of the stored session (same fallback as the backend). */
  private currentHourMinute(now: Date): string {
    const timezone = this.getCompanyTimezone();
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).format(now);
    } catch {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: DEFAULT_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).format(now);
    }
  }

  /** Same storage AbstractComponent.getTimezoneCompanyCurrentUser() reads. */
  private getCompanyTimezone(): string {
    try {
      const storageKey = this.constants?.['currentUser'];
      if (!storageKey) return DEFAULT_TIMEZONE;
      const stored = localStorage.getItem(storageKey);
      return (stored ? JSON.parse(stored)?.user?.company?.timezone : '') || DEFAULT_TIMEZONE;
    } catch {
      return DEFAULT_TIMEZONE;
    }
  }
}
